import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Car, Eye, Pencil } from "lucide-react";
import { formatEUR, formatDateFR, photoUrl } from "../../lib/api";
import { vehiclePrimaryLabel } from "../../lib/vehicleLabel";

export const BOOKING_STATUS = {
  confirmed: { bg: "bg-emerald-500", bgLight: "bg-emerald-100", text: "text-emerald-800", label: "Confirmée" },
  pending: { bg: "bg-amber-500", bgLight: "bg-amber-100", text: "text-amber-800", label: "En attente" },
  completed: { bg: "bg-slate-400", bgLight: "bg-slate-200", text: "text-slate-700", label: "Terminée" },
  cancelled: { bg: "bg-red-400", bgLight: "bg-red-100", text: "text-red-700", label: "Annulée" },
};

const VEHICLE_STATUS = {
  active: { label: "Disponible", cls: "bg-emerald-100 text-emerald-700" },
  available: { label: "Disponible", cls: "bg-emerald-100 text-emerald-700" },
  maintenance: { label: "Maintenance", cls: "bg-amber-100 text-amber-700" },
  inactive: { label: "Désactivé", cls: "bg-slate-200 text-slate-600" },
};

export function useHoverCard() {
  const [hover, setHover] = useState(null); // { type, item, extra, x, y }
  const timerRef = useRef(null);
  const cardRef = useRef(null);

  const cancelHoverClose = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  }, []);
  const scheduleHoverClose = useCallback(() => {
    cancelHoverClose();
    timerRef.current = setTimeout(() => setHover(null), 180);
  }, [cancelHoverClose]);
  const closeHover = useCallback(() => { cancelHoverClose(); setHover(null); }, [cancelHoverClose]);
  const openHover = useCallback((type, item, e, extra = null) => {
    cancelHoverClose();
    const r = e.currentTarget.getBoundingClientRect();
    setHover({ type, item, extra, x: r.left + r.width / 2, y: r.top });
  }, [cancelHoverClose]);

  useEffect(() => {
    if (!hover) return;
    const onKey = (e) => { if (e.key === "Escape") closeHover(); };
    const onDown = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) closeHover();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [hover, closeHover]);

  useEffect(() => () => cancelHoverClose(), [cancelHoverClose]);

  return { hover, cardRef, openHover, scheduleHoverClose, cancelHoverClose, closeHover };
}

export function HoverShell({ x, y, cardRef, onMouseEnter, onMouseLeave, children, width = 340 }) {
  return (
    <div
      ref={cardRef}
      style={{
        position: "fixed",
        left: Math.max(12, Math.min(x - width / 2, window.innerWidth - width - 12)),
        top: Math.max(12, y - 10),
        width,
        zIndex: 60,
        transform: "translateY(-100%)",
      }}
      className="bg-white border border-slate-200 rounded-2xl shadow-2xl p-4"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-testid="pilotage-hover-card"
    >
      {children}
    </div>
  );
}

function HRow({ label, val, mono }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-slate-400 shrink-0">{label}</span>
      <span className={`text-slate-700 text-right truncate ${mono ? "font-mono" : ""}`}>{val}</span>
    </div>
  );
}

export function BookingHoverCard({ booking: b, vehicle: v, onStatusChange, onClose }) {
  const navigate = useNavigate();
  const st = BOOKING_STATUS[b.status] || BOOKING_STATUS.confirmed;
  const days = Math.round((new Date(b.end_date + "T00:00:00") - new Date(b.start_date + "T00:00:00")) / 86400000) + 1;
  const go = (url) => { navigate(url); onClose(); };

  return (
    <div data-testid="booking-hover-card">
      <div className="flex items-start gap-3 mb-3">
        <div className="h-12 w-16 rounded-lg overflow-hidden bg-slate-100 shrink-0 flex items-center justify-center">
          {v?.photos?.[0] ? (
            <img src={photoUrl(v.photos[0])} alt="" className="h-full w-full object-cover" />
          ) : (
            <Car className="h-5 w-5 text-slate-300" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-heading font-medium text-sm text-slate-900 truncate">{b.client_name}</div>
          <div className="text-xs text-slate-500 truncate">{b.vehicle_name}</div>
          <span className={`inline-flex mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${st.bgLight} ${st.text}`} data-testid="hover-booking-status-badge">{st.label}</span>
        </div>
      </div>
      <div className="space-y-1.5 text-xs">
        {b.reference && <HRow label="Réf" val={b.reference} mono />}
        <HRow label="Dates" val={`${formatDateFR(b.start_date)} → ${formatDateFR(b.end_date)} · ${days} j`} />
        {(b.start_time || b.end_time) && <HRow label="Horaires" val={`${b.start_time || "—"} → ${b.end_time || "—"}`} />}
        {(b.pickup_location_name || b.return_location_name) && (
          <HRow
            label="Agence"
            val={!b.return_location_name || b.pickup_location_name === b.return_location_name
              ? (b.pickup_location_name || b.return_location_name)
              : `${b.pickup_location_name || "—"} → ${b.return_location_name}`}
          />
        )}
        {v?.license_plate && <HRow label="Plaque" val={v.license_plate} mono />}
        {b.client_phone && <HRow label="Téléphone" val={b.client_phone} />}
        {b.client_email && <HRow label="Email" val={b.client_email} />}
        <HRow label="Montant" val={`${formatEUR(b.total)}${b.price_per_day ? ` · ${formatEUR(b.price_per_day)}/j` : ""}`} />
      </div>
      <div className="mt-3 flex gap-1.5">
        {onStatusChange && (
          <select
            defaultValue={b.status}
            onChange={(e) => { onStatusChange(b.id, e.target.value); onClose(); }}
            onClick={(e) => e.stopPropagation()}
            className="flex-1 min-w-0 text-xs rounded-lg border border-slate-200 px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-violet-500"
            data-testid={`hover-booking-status-${b.id}`}
          >
            <option value="confirmed">Confirmée</option>
            <option value="pending">En attente</option>
            <option value="completed">Terminée</option>
            <option value="cancelled">Annulée</option>
          </select>
        )}
        <button
          onClick={() => go(`/vendor/pilotage?tab=bookings&booking=${b.id}`)}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800"
          data-testid={`hover-booking-view-${b.id}`}
        >
          <Eye className="h-3 w-3" /> Voir
        </button>
        <button
          onClick={() => go(`/vendor/pilotage?tab=bookings&booking=${b.id}&edit=1`)}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700"
          data-testid={`hover-booking-edit-${b.id}`}
        >
          <Pencil className="h-3 w-3" /> Modifier
        </button>
      </div>
    </div>
  );
}

export function VehicleHoverCard({ vehicle: v, onClose, catLabel }) {
  const navigate = useNavigate();
  const st = VEHICLE_STATUS[v.status] || VEHICLE_STATUS.active;
  const go = (url) => { navigate(url); onClose(); };
  const rawCat = catLabel ? catLabel(v.category) : (v.category || "");
  const catName = /^cat_/i.test(String(rawCat || "")) ? "" : rawCat;

  return (
    <div data-testid="vehicle-hover-card">
      <div className="h-24 -mx-4 -mt-4 mb-3 rounded-t-2xl overflow-hidden bg-slate-100 flex items-center justify-center">
        {v.photos?.[0] ? (
          <img src={photoUrl(v.photos[0])} alt="" className="h-full w-full object-cover" />
        ) : (
          <Car className="h-8 w-8 text-slate-300" />
        )}
      </div>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="font-heading font-medium text-sm text-slate-900 truncate">{vehiclePrimaryLabel(v)}</div>
          {catName && <div className="text-[10px] text-slate-500">{catName}</div>}
        </div>
        <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold ${st.cls}`}>{st.label}</span>
      </div>
      <div className="space-y-1.5 text-xs">
        {v.license_plate && <HRow label="Plaque" val={v.license_plate} mono />}
        {catName && <HRow label="Catégorie" val={catName} />}
        {v.city && <HRow label="Ville" val={v.city} />}
        {v.year && <HRow label="Année" val={v.year} />}
        {v.mileage != null && v.mileage !== "" && <HRow label="Kilométrage" val={`${Number(v.mileage).toLocaleString("fr-FR")} km`} />}
      </div>
      <div className="mt-3 flex gap-1.5">
        <button
          onClick={() => go(`/vendor/pilotage?tab=vehicles&vehicle=${v.id}`)}
          className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800"
          data-testid={`hover-vehicle-view-${v.id}`}
        >
          <Eye className="h-3 w-3" /> Voir la fiche
        </button>
        <button
          onClick={() => go(`/vendor/pilotage?tab=vehicles&edit=${v.id}`)}
          className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700"
          data-testid={`hover-vehicle-edit-${v.id}`}
        >
          <Pencil className="h-3 w-3" /> Modifier
        </button>
      </div>
    </div>
  );
}
